import { Icon } from '@captaincause/ui/Icon'
import { tm } from '@captaincause/ui/tm'
import { flattenImage } from 'utils/strapi/image'
import { Button } from 'components/Button'
import { StrapiImage } from 'components/StapiImage'
import type { StrapiApiImage } from 'types/strapi/image'
import type { StrapiLink } from 'types/strapi/link'

type Feature = {
  title: string | null
  description: string | null
  image: StrapiApiImage | null
}

export interface FeatureProps {
  title: string | null
  link: StrapiLink | null
  features: Feature[] | null
  withNumber?: boolean | null
}

export default function UseCases({
  title,
  link,
  features,
  withNumber = true,
}: FeatureProps) {
  return (
    <div className="mx-auto h-auto max-w-[1400px] overflow-hidden bg-white px-4 py-12 md:px-8 md:py-20">
      <div
        className={tm(
          'flex w-full flex-col items-center justify-between md:flex-row',
          !link?.url && 'md:justify-center',
        )}
      >
        {title ? (
          <h2
            className={tm(
              'order-2 mt-4 max-w-[450px] flex-1 text-center text-[40px] font-semibold md:order-[initial] md:mt-0 md:text-left',
              !link?.url && 'md:text-center',
            )}
          >
            {title}
          </h2>
        ) : null}
        {link?.url ? (
          <Button
            className={tm('order-1 w-full md:order-[initial] md:w-[unset]')}
            href={link.url}
            variant="secondary"
            iconRight="arrow-go"
          >
            {link.title}
          </Button>
        ) : null}
      </div>

      {features ? (
        <div
          className={tm(
            'mx-auto flex flex-col flex-wrap items-stretch justify-center space-y-4 pt-10 md:flex-row md:space-x-4 md:space-y-4 md:pt-14 lg:space-x-8 lg:space-y-0',
            features.length > 2 && 'md:justify-between',
          )}
        >
          {features.map((feature, index) => {
            const image = flattenImage(feature.image)

            return (
              <div
                key={feature.title}
                className={tm(
                  'flex w-full min-w-[200px] flex-1 flex-col rounded-2xl border border-inside p-4 pt-8 md:flex-[48%] lg:flex-[30%]',
                  features.length < 3 && 'md:max-w-[450px]',
                  index === 2 && 'md:!ml-0 md:flex-1 lg:!ml-8 lg:flex-[30%]',
                  index !== 2 && 'md:!mt-0',
                )}
              >
                {withNumber ? (
                  <div className="mb-4 flex items-baseline justify-center space-x-1">
                    <Icon icon="cursor" fill="fill-black" />
                    <span className="text-6xl font-extrabold">{index + 1}</span>
                  </div>
                ) : null}
                {feature.title ? (
                  <h3 className="mb-4 text-center text-2xl">{feature.title}</h3>
                ) : null}
                {feature.description ? (
                  <p className="mb-4 text-center leading-7">
                    {feature.description}
                  </p>
                ) : null}
                <div className="flex h-full items-center justify-center">
                  {image ? (
                    <StrapiImage
                      {...image}
                      className={tm(
                        'relative z-[1] mx-auto max-h-[450px] w-full max-w-[350px] rounded-xl object-contain',
                      )}
                    />
                  ) : null}
                </div>
              </div>
            )
          })}
        </div>
      ) : null}
    </div>
  )
}
